import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { AlertCircle, Loader2 } from 'lucide-react';

import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';

/**
 * Component for rendering processFiles tool output. See fill-sl1-form-tool.tsx for why
 * this is intentionally a no-op — generated files are hoisted at the message level.
 */
export function ProcessFilesTool(_props: { part: unknown; messageUuid: string }) {
  return null;
}

export interface GeneratedFileListItem {
  uuid: string | null;
  filename: string;
  isPending?: boolean;
  failed?: boolean;
  failureReason?: string | null;
}

const MAX_FAILURE_REASON_TITLE_LENGTH = 100;

function getFailureTitle(failureReason?: string | null) {
  return (failureReason ?? 'File unavailable').slice(0, MAX_FAILURE_REASON_TITLE_LENGTH);
}

export function GeneratedFilesList({
  files,
  messageUuid,
  source = 'generated_file',
}: {
  files: GeneratedFileListItem[];
  messageUuid?: string;
  source?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-muted-foreground text-sm font-medium">Generated Files:</div>
      <div className="flex flex-row flex-wrap gap-2">
        {files.map((file) => (
          <ChatFileListItem
            key={`${file.filename}-${file.uuid ?? 'pending'}`}
            file={file}
            messageUuid={messageUuid}
            source={source}
          />
        ))}
      </div>
    </div>
  );
}

function ChatFileListItem({
  file,
  messageUuid,
  source,
}: {
  file: GeneratedFileListItem;
  messageUuid?: string;
  source: string;
}) {
  const { download, isDownloading } = useDownloadFile({});
  const analytics = useAnalytics();
  const isFailed = Boolean(file.failed);
  const isPending = !isFailed && (file.isPending || !file.uuid);
  const isDisabled = isPending || isDownloading || isFailed || !file.uuid;
  const failureTitle = isFailed ? getFailureTitle(file.failureReason) : undefined;

  const handleClick = () => {
    if (file.uuid && !isFailed) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
        fileExtension: getFileExtension(file.filename),
        fileUuid: file.uuid,
        messageUuid,
        source,
      });
      download(file.uuid);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && file.uuid && !isFailed) {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-disabled={isDisabled}
      aria-busy={isPending || isDownloading}
      title={failureTitle}
      className={cn(
        'flex w-fit max-w-xs cursor-pointer items-center gap-2 rounded-sm px-1 py-1',
        'hover:bg-muted/50 transition-colors',
        {
          'pointer-events-none opacity-50': isDisabled,
          'animate-pulse': isPending,
        },
      )}
    >
      <FileIcon filename={file.filename} hasBackground={false} />
      <span className="text-steel-400 truncate text-xs">{file.filename}</span>
      {isPending && <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />}
      {isFailed && (
        <>
          <AlertCircle className="text-destructive h-3.5 w-3.5" />
          <span className="text-destructive text-xs" data-testid="generated-file-unavailable">
            Unavailable
          </span>
        </>
      )}
    </div>
  );
}
